import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Alert,
} from "reactstrap";
import Flatpickr from "react-flatpickr";

const EditScheduleModal = ({
  isOpen,
  onClose,
  schedule,
  onUpdate,
  isAddMode = false,
}) => {
  const [formData, setFormData] = useState({
    campaignId: 0,
    startDate: null,
    endDate: null,
    sunday: false,
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    startTime: "00:00:00",
    endTime: "23:59:59",
    isActive: true,
  });
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (schedule) {
      setFormData({
        campaignId: schedule.campaignId || 0,
        startDate: schedule.startDate ? [new Date(schedule.startDate)] : null,
        endDate: schedule.endDate ? [new Date(schedule.endDate)] : null,
        sunday: schedule.sunday || false,
        monday: schedule.monday || false,
        tuesday: schedule.tuesday || false,
        wednesday: schedule.wednesday || false,
        thursday: schedule.thursday || false,
        friday: schedule.friday || false,
        saturday: schedule.saturday || false,
        startTime: schedule.startTime || "00:00:00",
        endTime: schedule.endTime || "23:59:59",
        isActive: schedule.isActive !== undefined ? schedule.isActive : true,
      });
    }
  }, [schedule]);

  const handleToggleDay = (day) => {
    setFormData((prev) => ({
      ...prev,
      [day]: !prev[day],
    }));
  };

  const handleSubmit = async () => {
    const newErrors = [];

    if (!formData.startDate || formData.startDate.length === 0) {
      newErrors.push("Start date is required");
    }

    const hasSelectedDay = [
      formData.sunday,
      formData.monday,
      formData.tuesday,
      formData.wednesday,
      formData.thursday,
      formData.friday,
      formData.saturday,
    ].some((day) => day);

    if (!hasSelectedDay) {
      newErrors.push("Please select at least one day");
    }

    if (newErrors.length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        campaignId: formData.campaignId,
        startDate: formData.startDate[0].toISOString(),
        endDate:
          formData.endDate && formData.endDate.length > 0
            ? formData.endDate[0].toISOString()
            : null,
        sunday: formData.sunday,
        monday: formData.monday,
        tuesday: formData.tuesday,
        wednesday: formData.wednesday,
        thursday: formData.thursday,
        friday: formData.friday,
        saturday: formData.saturday,
        startTime: formData.startTime,
        endTime: formData.endTime,
        isActive: formData.isActive,
      };

      await onUpdate(schedule?.id || null, payload);
      handleClose();
    } catch (error) {
      setErrors([
        error.message ||
          `Failed to ${isAddMode ? "create" : "update"} schedule`,
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setErrors([]);
    onClose();
  };

  const weekDays = [
    { key: "sunday", label: "Sun" },
    { key: "monday", label: "Mon" },
    { key: "tuesday", label: "Tue" },
    { key: "wednesday", label: "Wed" },
    { key: "thursday", label: "Thu" },
    { key: "friday", label: "Fri" },
    { key: "saturday", label: "Sat" },
  ];

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg" centered>
      <ModalHeader toggle={handleClose}>
        {isAddMode ? "Create Campaign Schedule" : "Edit Campaign Schedule"}
      </ModalHeader>
      <ModalBody>
        {errors.length > 0 && (
          <Alert color="danger" className="mb-3">
            {errors.map((error, idx) => (
              <div key={idx}>{error}</div>
            ))}
          </Alert>
        )}

        <div className="mb-3 p-3 bg-light rounded">
          <strong>Campaign:</strong> {schedule?.campaignName || "N/A"}
        </div>

        <FormGroup>
          <Label className="fw-semibold">
            Start Date & Time <span className="text-danger">*</span>
          </Label>
          <Flatpickr
            value={formData.startDate}
            onChange={(date) =>
              setFormData((prev) => ({ ...prev, startDate: date }))
            }
            // options={{
            // enableTime: true,
            // dateFormat: "Y-m-d H:i",
            // time_24hr: false,
            // }}
            placeholder="Select start date and time"
            className="form-control"
          />
        </FormGroup>

        <FormGroup>
          <Label className="fw-semibold">End Date & Time</Label>
          <Flatpickr
            value={formData.endDate}
            onChange={(date) =>
              setFormData((prev) => ({ ...prev, endDate: date }))
            }
            options={{
              // enableTime: true,
              // dateFormat: "Y-m-d H:i",
              minDate:
                formData.startDate && formData.startDate.length > 0
                  ? formData.startDate[0]
                  : "today",
              // time_24hr: false,
            }}
            placeholder="Select end date and time (optional)"
            className="form-control"
          />
        </FormGroup>

        <FormGroup>
          <Label className="fw-semibold">
            Recurring Days <span className="text-danger">*</span>
          </Label>
          <div className="d-flex gap-2 flex-wrap">
            {weekDays.map((day) => (
              <Button
                key={day.key}
                color={formData[day.key] ? "primary" : "outline-secondary"}
                onClick={() => handleToggleDay(day.key)}
                size="sm"
                style={{
                  minWidth: "45px",
                  fontWeight: formData[day.key] ? "600" : "normal",
                }}
              >
                {day.label}
              </Button>
            ))}
          </div>
        </FormGroup>

        <FormGroup>
          <Label className="fw-semibold">Start Time</Label>
          <Input
            type="time"
            step="1"
            value={formData.startTime}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, startTime: e.target.value }))
            }
            className="form-control"
          />
        </FormGroup>

        <FormGroup>
          <Label className="fw-semibold">End Time</Label>
          <Input
            type="time"
            step="1"
            value={formData.endTime}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, endTime: e.target.value }))
            }
            className="form-control"
          />
        </FormGroup>

        <FormGroup check>
          <Input
            type="checkbox"
            checked={formData.isActive}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, isActive: e.target.checked }))
            }
            id="isActiveCheck"
          />
          <Label check for="isActiveCheck" className="fw-semibold">
            Active Schedule
          </Label>
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button color="primary" onClick={handleSubmit} disabled={loading}>
          {loading ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
              />
              {isAddMode ? "Creating..." : "Updating..."}
            </>
          ) : isAddMode ? (
            "Create Schedule"
          ) : (
            "Save Changes"
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default EditScheduleModal;
